import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  TrendingUp, Eye, Heart, MessageCircle, Users, UserPlus,
  Radio, Gift, Trophy, Sparkles, Play, ArrowUpRight, CheckCircle2, XCircle,
  BarChart3, Crown, Wallet, Edit3, Video, ShoppingBag, Trash2,
} from "lucide-react";

const PERIODS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "all", label: "All time" },
];

const fmtNum = (n) => {
  if (n == null) return "0";
  if (n >= 1e7) return (n / 1e7).toFixed(1) + "Cr";
  if (n >= 1e5) return (n / 1e5).toFixed(1) + "L";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
};

const fmtDuration = (mins) => {
  if (!mins) return "0m";
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins}m`;
};

function StatCard({ icon: Icon, label, value, sub, color = "#00E5FF", testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl p-3 border border-white/10 bg-white/5 relative overflow-hidden">
      <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-15" style={{ background: color }}/>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }}/>
        <span className="text-[10px] uppercase tracking-wider font-bold text-white/60">{label}</span>
      </div>
      <div className="font-outfit font-extrabold text-2xl tracking-tight" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniBar({ data, color = "#00E5FF" }) {
  const max = Math.max(1, ...data.map(d => d.views || 0));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="dash-views-chart">
      {data.map((d, i) => {
        const h = Math.max(2, Math.round((d.views / max) * 100));
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            <div className="w-full rounded-t-sm transition-all" style={{ height: `${h}%`, background: color, opacity: 0.7 + (i / data.length) * 0.3 }}/>
            <span className="text-[7px] text-white/40 -rotate-45 origin-top-left mt-1 whitespace-nowrap">{d.date}</span>
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-black/90 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              {d.date}: {fmtNum(d.views)} views
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    setLoading(true);
    api.get(`/me/insights?period=${period}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  if (loading || !data) {
    return (
      <div className="px-4 py-10 text-center text-white/50" data-testid="dash-loading">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 animate-pulse text-[#00E5FF]"/>
        Loading your creator insights…
      </div>
    );
  }

  const { reach, audience, content, live, earnings, monetization, top_reels, daily_series } = data;

  return (
    <div className="px-4 pt-5 pb-24" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="font-outfit font-extrabold text-3xl tracking-tighter flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-[#00E5FF]"/>
            Creator Dashboard
          </h1>
          <p className="text-xs text-white/50 mt-0.5">Your organic reach, engagement and monetization at a glance</p>
        </div>
      </div>

      {/* Admin-only management tiles */}
      {isAdmin && (
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 to-[#FF2A5F]/5 border border-[#FF7A00]/30 p-3" data-testid="dash-admin-section">
          <div className="flex items-center gap-1.5 mb-2">
            <Crown className="w-4 h-4 text-[#FF7A00]"/>
            <h2 className="text-[11px] uppercase tracking-widest text-[#FF7A00] font-bold">Admin · Premium Management</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Link to="/admin/premium-profiles" data-testid="dash-admin-premium-profiles" className="p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-95 transition">
              <Crown className="w-5 h-5 text-[#FF7A00] mb-1"/>
              <div className="font-bold text-xs">Premium Profiles</div>
              <div className="text-[9px] text-white/60 mt-0.5">Edit / delete profiles</div>
            </Link>
            <Link to="/admin/premium-content" data-testid="dash-admin-premium-content" className="p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-95 transition">
              <Video className="w-5 h-5 text-[#00E5FF] mb-1"/>
              <div className="font-bold text-xs">Premium Content</div>
              <div className="text-[9px] text-white/60 mt-0.5">Add / remove videos</div>
            </Link>
            <Link to="/admin/templates" data-testid="dash-admin-templates" className="p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-95 transition">
              <ShoppingBag className="w-5 h-5 text-[#A855F7] mb-1"/>
              <div className="font-bold text-xs">Templates Store</div>
              <div className="text-[9px] text-white/60 mt-0.5">Add / edit / price</div>
            </Link>
          </div>
        </div>
      )}

      {/* Period selector */}
      <div className="flex items-center gap-1.5 my-4 overflow-x-auto no-scrollbar" data-testid="dash-period-tabs">
        {PERIODS.map(p => (
          <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`dash-period-${p.id}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition ${period === p.id
              ? "bg-[#00E5FF] text-black border-[#00E5FF]"
              : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Reach (Impressions / Engagement) */}
      <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5"/>Reach &amp; Engagement</h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <StatCard testId="stat-impressions" icon={Eye} label="Impressions" value={fmtNum(reach.total_impressions)}
          sub={period !== "all" ? `+${fmtNum(reach.period_impressions)} this period` : "All-time views"} color="#00E5FF"/>
        <StatCard testId="stat-engagement" icon={Heart} label="Engagement" value={`${reach.engagement_rate_pct}%`}
          sub={`${fmtNum(reach.total_likes)} likes • ${fmtNum(reach.total_comments)} comments`} color="#FF2A5F"/>
        <StatCard testId="stat-likes" icon={Heart} label="Total likes" value={fmtNum(reach.total_likes)}
          sub={period !== "all" ? `+${fmtNum(reach.period_likes)} this period` : "All-time"} color="#FB7185"/>
        <StatCard testId="stat-comments" icon={MessageCircle} label="Comments" value={fmtNum(reach.total_comments)}
          sub={period !== "all" ? `+${fmtNum(reach.period_comments)} this period` : "All-time"} color="#A855F7"/>
      </div>

      {/* Views trend chart */}
      <div className="rounded-2xl p-4 border border-white/10 bg-gradient-to-br from-[#00E5FF]/5 to-transparent mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-[#00E5FF]"/>Views — last 14 days</h3>
          <span className="text-[10px] text-white/50">By upload day</span>
        </div>
        <MiniBar data={daily_series} color="#00E5FF"/>
      </div>

      {/* Audience growth */}
      <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5"/>Audience Growth</h2>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard testId="stat-followers" icon={Users} label="Followers" value={fmtNum(audience.followers)} color="#FFD700"/>
        <StatCard testId="stat-new-followers" icon={UserPlus} label="New" value={`+${fmtNum(audience.new_followers_in_period)}`}
          sub="In selected period" color="#34D399"/>
        <StatCard testId="stat-following" icon={Users} label="Following" value={fmtNum(audience.following)} color="#9CA3AF"/>
      </div>

      {/* Content stats */}
      <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><Play className="w-3.5 h-3.5"/>Content Output</h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <StatCard testId="stat-total-reels" icon={Play} label="Total reels" value={fmtNum(content.total_reels)}
          sub={`${fmtNum(content.reels_in_period)} in period`} color="#00E5FF"/>
        <StatCard testId="stat-viral" icon={Sparkles} label="Viral reels" value={`${content.viral_reels} / ${content.viral_target}`}
          sub="Hit 1L+ views" color="#FFD700"/>
      </div>

      {/* Live stats */}
      {(live.total_streams > 0 || live.total_stream_minutes > 0) && (
        <>
          <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><Radio className="w-3.5 h-3.5"/>Live Streaming</h2>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <StatCard testId="stat-streams" icon={Radio} label="Streams" value={fmtNum(live.total_streams)} color="#FF2A5F"/>
            <StatCard testId="stat-stream-time" icon={Trophy} label="Stream time" value={fmtDuration(live.total_stream_minutes)} color="#FFD700"/>
            <StatCard testId="stat-stream-viewers" icon={Eye} label="Viewers" value={fmtNum(live.total_stream_viewers)} color="#00E5FF"/>
            <StatCard testId="stat-stream-level" icon={Crown} label="Stream level" value={`Lv ${live.stream_level}`}
              sub={`${live.stream_badge?.toUpperCase()} • ${fmtNum(live.stream_xp)} XP`} color="#A855F7"/>
          </div>
        </>
      )}

      {/* Earnings */}
      <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5"/>Earnings &amp; Wallet</h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <StatCard testId="stat-gifts-received" icon={Gift} label="Gifts received" value={fmtNum(earnings.total_gifts_received_amount)}
          sub="Goldly value earned from gifts" color="#FFD700"/>
        <StatCard testId="stat-wallet-goldly" icon={Sparkles} label="Wallet Goldly" value={fmtNum(earnings.wallet_goldly)} color="#FFD700"/>
        <StatCard testId="stat-wallet-tokwin" icon={Sparkles} label="Wallet Tokwin" value={fmtNum(earnings.wallet_tokwin)} color="#00E5FF"/>
        <StatCard testId="stat-gifts-sent" icon={Gift} label="Gifts sent" value={fmtNum(earnings.total_gifts_sent_amount)} color="#FB7185"/>
      </div>

      {/* Monetization eligibility */}
      <div className="rounded-2xl p-4 border border-[#FF7A00]/30 bg-gradient-to-br from-[#FF7A00]/10 to-transparent mb-4" data-testid="monetization-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-outfit font-bold text-base flex items-center gap-1.5"><Crown className="w-4 h-4 text-[#FF7A00]"/>Monetization Eligibility</h3>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${monetization.is_qualified ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/70"}`}>
            {monetization.is_qualified ? "Qualified ✓" : `${monetization.progress_pct}% complete`}
          </span>
        </div>
        <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden mb-3">
          <div className="h-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] transition-all" style={{ width: `${monetization.progress_pct}%` }}/>
        </div>
        <div className="space-y-1.5 text-xs">
          {Object.entries(monetization.criteria).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between" data-testid={`crit-${k}`}>
              <div className="flex items-center gap-1.5">
                {v.met ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <XCircle className="w-4 h-4 text-white/30"/>}
                <span className={v.met ? "text-white/90" : "text-white/60"}>
                  {k === "min_followers" && "1,000+ followers"}
                  {k === "min_total_views" && "10,000+ total views"}
                  {k === "min_account_age_days" && "30+ days account age"}
                  {k === "min_uploads_last_30d" && "5+ uploads in last 30 days"}
                </span>
              </div>
              <span className="font-bold text-[#FF7A00]">{fmtNum(v.current)}<span className="text-white/40 font-normal"> / {fmtNum(v.required)}</span></span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-white/50 mt-3 leading-relaxed">
          Once all 4 criteria are met you can apply for direct monetization payouts via Help &amp; Support.
          Faster path: get 5 reels past 1 lakh views (Go-Viral track).
        </p>
      </div>

      {/* Top reels */}
      {top_reels.length > 0 && (
        <>
          <h2 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mt-4 mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5"/>Top Performing Reels</h2>
          <div className="space-y-2 mb-4" data-testid="top-reels-list">
            {top_reels.map((r, i) => (
              <div key={r.id} data-testid={`top-reel-${i}`}
                className="flex items-center gap-2.5 p-2 rounded-2xl border border-white/10 bg-white/5">
                <Link to="/" className="relative w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-white/10"
                  style={r.thumbnail_url ? { backgroundImage: `url(${r.thumbnail_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                  <div className="absolute top-0.5 left-0.5 text-[9px] font-extrabold text-[#FF7A00] bg-black/60 px-1 rounded">#{i+1}</div>
                </Link>
                <Link to="/" className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-white/95 line-clamp-1">{r.title || "Untitled reel"}</div>
                  <div className="flex items-center gap-2.5 text-[10px] text-white/60 mt-0.5">
                    <span className="flex items-center gap-0.5"><Eye className="w-3 h-3"/>{fmtNum(r.views)}</span>
                    <span className="flex items-center gap-0.5"><Heart className="w-3 h-3 text-[#FF2A5F]"/>{fmtNum(r.likes)}</span>
                    <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3"/>{fmtNum(r.comments)}</span>
                  </div>
                </Link>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete "${r.title || 'this reel'}" permanently?`)) return;
                    try {
                      await api.delete(`/reels/${r.id}`);
                      window.location.reload();
                    } catch (e) {
                      window.alert("Delete failed: " + (e?.response?.data?.detail || e?.message || ""));
                    }
                  }}
                  data-testid={`dash-delete-reel-${r.id}`}
                  className="p-2 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/30 hover:bg-[#FF2A5F]/25 active:scale-90"
                  title="Delete reel">
                  <Trash2 className="w-3.5 h-3.5 text-[#FF2A5F]"/>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="text-[10px] text-center text-white/40 mt-6">
        Insights refresh in real time. Data as of {new Date(data.as_of).toLocaleString()}.
      </p>
    </div>
  );
}
